/**
 * Auth API for investors.nurev.io.
 *
 * Uses the existing legacy accept-invite + email/password login flow
 * already supported by nurev-api (no new backend endpoints). The admin
 * cap table controller's `inviteShareholder` issues a single-use
 * `inviteToken` (14-day expiry, `mustChangePassword: true`); the
 * investor lands here, sets their password, gets back a JWT, and
 * thereafter logs in with email + password.
 *
 * Path note: nurev-api mounts `app/routes/auth.js` at root (`/`), so
 * the auth endpoints live at `/login`, `/accept-invite`, etc. — NOT
 * under an `/auth/*` prefix. (Older drafts of this client had `/auth/*`
 * which 404'd in prod.)
 *
 * Magic-link can come later as a backend-side enhancement — once
 * inflight invites are claimed.
 */
import { client, unwrap } from './client';

export interface InvestorSummary {
  id?: string;
  name: string;
  email: string;
  role: 'shareholder' | 'admin';
}

export interface LookupInviteResponse {
  name: string;
  email: string;
  role: 'shareholder' | 'admin';
}

/**
 * GET /accept-invite/:token
 * Validates the invite token + returns who it's for. No JWT yet — the
 * UI uses this to render "Welcome, {name}" before the password set.
 */
export async function lookupInvite(token: string): Promise<LookupInviteResponse> {
  return unwrap(client.get(`/accept-invite/${token}`));
}

/**
 * POST /accept-invite
 * Exchanges the single-use invite token + the chosen password for a
 * 7-day JWT. Server-side clears inviteToken, sets mustChangePassword
 * false, hashes the password via pre-save hook.
 *
 * NOTE: this endpoint returns a FLAT `{ token, user }` body — NOT the
 * `{ success, data: { ... } }` envelope that most NUREV endpoints use.
 * So we read `r.data` directly instead of going through `unwrap()`.
 * (Earlier versions of this client called `unwrap()` here, which
 * silently returned `undefined`, breaking the destructure in
 * AcceptInviteScreen — that was the root cause of the shareholder
 * portal incident on 2026-05-28.)
 */
export async function acceptInvite(input: {
  token: string;
  password: string;
}): Promise<{ token: string; user: InvestorSummary }> {
  const r = await client.post('/accept-invite', input);
  return r.data;
}

/**
 * POST /login
 * Standard email + password login. Same flat `{ token, user }` shape as
 * acceptInvite — read `r.data` directly, do NOT use `unwrap()`.
 */
export async function login(input: {
  email: string;
  password: string;
}): Promise<{ token: string; user: InvestorSummary }> {
  const r = await client.post('/login', input);
  return r.data;
}
